import React, { useState, useEffect } from 'react';
import { Anchor } from 'lucide-react';
import heroVideo from '../../assets/hero.mp4'; // Make sure to place your video in the correct path and import it here.

// Custom Animation Wrapper
const FadeIn = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div 
      className={`transition-all duration-1000 ease-out transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {children}
    </div>
  );
};

export default function Hero() {
  return (
    <div className="pt-24 min-h-[90vh] flex flex-col relative overflow-hidden">
      
      {/* --- VIDEO BACKGROUND --- */}
      <video 
        autoPlay 
        loop 
        muted 
        playsInline
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
      >
        <source src={heroVideo} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* --- OVERLAY --- 
          This sits on top of the video to darken it so your white text is readable.
          It uses your brand's Slate Blue (#1E4363) at 70% opacity. */}
      <div className="absolute inset-0 bg-[#1E4363]/70 z-0"></div>

      {/* --- HERO CONTENT --- (Must have z-10 so it sits on top of the video) */}
      <div className="flex-grow flex flex-col justify-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10 py-20">
        <div className="max-w-3xl">
          <FadeIn delay={100}>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#87412A]/40 border border-[#87412A] text-[#F9F8F3] rounded-full text-sm font-medium mb-6 backdrop-blur-sm">
              <Anchor size={14} /> Excellence in Every Mile
            </div>
          </FadeIn>
          
          <FadeIn delay={300}>
            <h2 className="text-5xl md:text-7xl font-bold text-[#F9F8F3] leading-tight mb-6 font-serif drop-shadow-lg">
              Trusted Maritime <br/>
              <span className="text-[#87412A]">Solutions</span> Worldwide.
            </h2>
          </FadeIn>
          
          <FadeIn delay={500}>
            <p className="text-lg md:text-xl text-[#F9F8F3]/90 mb-10 max-w-2xl leading-relaxed drop-shadow-md">
              Delivering accurate vessel assessments, independent oversight, and comprehensive maritime consultancy to safeguard your assets and operations.
            </p>
          </FadeIn>
          
          <FadeIn delay={700}>
            <div className="flex flex-wrap gap-4">
              
              {/* UPDATED: Explore Services Link */}
              <a 
                href="/services" 
                className="bg-[#87412A] text-[#F9F8F3] px-8 py-3.5 rounded-sm font-medium hover:bg-[#6b3321] transition-all shadow-lg hover:-translate-y-1 inline-block text-center"
              >
                Explore Services
              </a>
              
              {/* UPDATED: Corporate Profile Link */}
              <a 
                href="/about" 
                className="bg-transparent border border-[#F9F8F3] text-[#F9F8F3] px-8 py-3.5 rounded-sm font-medium hover:bg-[#F9F8F3] hover:text-[#1E4363] hover:border-transparent transition-all backdrop-blur-sm inline-block text-center"
              >
                Our Corporate Profile
              </a>

            </div>
          </FadeIn>
        </div>
      </div>

      {/* --- TRUST / STATS BAR --- */}
      <div className="bg-[#2C3539]/90 backdrop-blur-md w-full mt-auto py-8 relative z-10 shadow-2xl border-t-4 border-[#87412A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-gray-600">
            <div className="text-center px-4 hover:scale-105 transition-transform">
              <h3 className="text-3xl font-bold text-[#87412A] drop-shadow-sm">5+</h3>
              <p className="text-[#F9F8F3]/80 text-sm mt-1 uppercase tracking-wider font-medium">Years Experience</p>
            </div>
            <div className="text-center px-4 hover:scale-105 transition-transform">
              <h3 className="text-3xl font-bold text-[#87412A] drop-shadow-sm">30+</h3>
              <p className="text-[#F9F8F3]/80 text-sm mt-1 uppercase tracking-wider font-medium">Major Ports</p>
            </div>
            <div className="text-center px-4 hover:scale-105 transition-transform">
              <h3 className="text-3xl font-bold text-[#87412A] drop-shadow-sm">100+</h3>
              <p className="text-[#F9F8F3]/80 text-sm mt-1 uppercase tracking-wider font-medium">Surveys Completed</p>
            </div>
            <div className="text-center px-4 hover:scale-105 transition-transform">
              <h3 className="text-3xl font-bold text-[#87412A] drop-shadow-sm">24/7</h3>
              <p className="text-[#F9F8F3]/80 text-sm mt-1 uppercase tracking-wider font-medium">Global Support</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}